import { useState } from 'react';
import { Icon } from '@/components/Icon';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { showToast } from '@/lib/toast';
import { deleteOperatorTemplate, setDefaultOperatorTemplate, type OperatorTemplateRow } from '@/api/operatorTemplates';
import { OperatorTemplateMenu } from './OperatorTemplateMenu';

/**
 * Плитка шаблона — 1:1 строка OperatorListLayout::columns() старого Orchid,
 * но раскрывающаяся, а не табличная (шаблонов мало, полноценная таблица со
 * скроллом ради двух колонок избыточна — тот же довод, что и у CatalogTile
 * в TestsCatalogPage.tsx). Клик по названию/шеврону — "ознакомиться" (просто
 * раскрыть текст соглашения), три точки — редактирование/удаление/умолчание.
 * Само редактирование — отдельный экран (см. DataOperatorsPage.onEditRequest),
 * не раскрытие тут же в плитке — без списка и поиска рядом.
 */
export function OperatorTemplateTile({
  template,
  expanded,
  onToggleExpand,
  onEditRequest,
  onSaved,
  onDeleted,
}: {
  template: OperatorTemplateRow;
  /** Раскрытие держит страница, а не плитка: раскрытым должен быть ровно один шаблон. */
  expanded: boolean;
  onToggleExpand: () => void;
  onEditRequest: () => void;
  onSaved: (updated: OperatorTemplateRow) => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const { confirm, dialog } = useConfirmDialog();

  const toggleExpand = onToggleExpand;

  const handleDelete = async () => {
    const ok = await confirm(`Удалить шаблон «${template.name}»? Это действие необратимо.`, {
      confirmLabel: 'Удалить',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteOperatorTemplate(template.id);
      showToast('Шаблон удалён');
      onDeleted();
    } catch {
      showToast('Не удалось удалить шаблон.');
      setBusy(false);
    }
  };

  const handleSetDefault = async () => {
    setBusy(true);
    try {
      const updated = await setDefaultOperatorTemplate(template.id);
      onSaved(updated);
      showToast('Шаблон по умолчанию установлен');
    } catch {
      showToast('Не удалось установить шаблон по умолчанию.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`catalog-tile${expanded ? ' catalog-tile--expanded' : ''}`}>
      <div className="catalog-tile__main">
        <div className="catalog-tile__icon">
          <Icon name="fileText" size={18} />
        </div>
        <button type="button" className="catalog-tile__body catalog-tile__body--clickable" onClick={toggleExpand}>
          <div className="catalog-tile__name">
            {template.name}
            {template.default && <span className="badge badge--info">По умолчанию</span>}
          </div>
          {template.description && <div className="catalog-tile__category">{template.description}</div>}
        </button>

        <OperatorTemplateMenu
          disabled={busy}
          isDefault={template.default}
          onEdit={onEditRequest}
          onDelete={handleDelete}
          onSetDefault={handleSetDefault}
        />

        <button
          type="button"
          className="catalog-tile__expand"
          onClick={toggleExpand}
          aria-expanded={expanded}
          aria-label={expanded ? `Скрыть «${template.name}»` : `Показать «${template.name}»`}
        >
          <Icon name="chevronDown" size={16} />
        </button>
      </div>

      {expanded && (
        <div className="catalog-tile__reports">
          {/* HTML набран через RichTextEditor тем же кругом админов (platform.operator) — доверяем ему так же, как раньше доверяли выводу Quill в Orchid. */}
          <div className="rich-text-preview" dangerouslySetInnerHTML={{ __html: template.text }} />
        </div>
      )}

      {dialog}
    </div>
  );
}
